import { Link } from "react-router-dom";
import Hero from "@/components/Hero";
import MarqueeStrip from "@/components/MarqueeStrip";
// import Manifesto from "@/components/Manifesto";
import FeatureGrid from "@/components/FeatureGrid";
import WorkflowTimeline from "@/components/WorkflowTimeline";
import PersonasGrid from "@/components/PersonasGrid";
import SecurityGrid from "@/components/SecurityGrid";
import PricingCards from "@/components/PricingCards";
import CTA from "@/components/CTA";
import SectionTitle from "@/components/SectionTitle";
import { APP_URL } from "@/lib/urls";
import { ArrowUpRight, Cloud, DatabaseZap, MessageSquare } from "lucide-react";
import { motion } from "framer-motion";

const WHY = [
  { icon: DatabaseZap, title: "All your data, one place", desc: "Every case, connected to the rest of your operation." },
  { icon: MessageSquare, title: "Nothing gets lost", desc: "Every question, decision, and file stays with the case" },
  { icon: Cloud, title: "Cloud access", desc: "Open any case from anywhere, on any device." },
];

export default function Home() {
  return (
    <>
      <Hero />
      <MarqueeStrip />

      {/* Platform overview strip */}
      <section data-testid="platform-overview" className="relative py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionTitle
            eyebrow="Platform overview"
            title="One connected system, for the whole case."
            description="Everyone on your team, from the front desk to the lab, works off the same case."
          />

          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              "Prescriptions",
              "Production",
              "Communication",
              "Billing",
              "Reporting",
              "Analytics",
            ].map((t, i) => (
              <motion.div
                key={t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                data-testid={`overview-chip-${t.toLowerCase()}`}
                className="rounded-2xl hairline glass px-4 py-4 flex flex-col items-start gap-2"
              >
                <span className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground font-mono">
                  {i + 1}
                </span>
                <div className="font-heading font-semibold text-sm">{t}</div>
                <div className="h-0.5 w-6 rounded-full bg-brand-gradient" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Rxn3D */}
      <section data-testid="why-rxn3d" className="relative py-16 sm:py-20 bg-secondary/30 dark:bg-secondary/20 border-y hairline">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionTitle
            eyebrow="Why Rxn3D"
            title="Built to make your day easier."
          />

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {WHY.map((w, i) => {
              const Icon = w.icon;
              return (
                <motion.div
                  key={w.title}
                  data-testid={`why-card-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  whileHover={{ y: -4 }}
                  className="group rounded-2xl hairline bg-background p-6 relative overflow-hidden"
                >
                  <div className="h-10 w-10 rounded-xl hairline inline-flex items-center justify-center mb-4">
                    <Icon className="h-4 w-4 text-brand-plum" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold">{w.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-10 flex justify-center">
            <a
              href={APP_URL}
              data-testid="why-cta-create-account"
              className="group inline-flex items-center gap-2 pl-6 pr-4 py-3.5 rounded-full bg-foreground text-background font-semibold text-sm hover:opacity-90 transition-opacity"
            >
              Create your Rxn3D Account
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand-gradient text-white transition-transform group-hover:translate-x-0.5">
                <ArrowUpRight className="h-3.5 w-3.5" />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Manifesto (numbered chapters) temporarily disabled, keep for later */}
      {/* <Manifesto /> */}

      {/* Core features */}
      <FeatureGrid />

      <WorkflowTimeline />
      <PersonasGrid />
      <SecurityGrid />
      <PricingCards />

      <section data-testid="faq-teaser" className="relative py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <SectionTitle
            align="center"
            eyebrow="FAQ"
            title="Frequently asked."
            description="Everything you'd want to know before requesting a demo."
          />
          <Link
            to="/faq"
            data-testid="faq-teaser-link"
            className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-full hairline glass text-sm font-medium hover:bg-secondary/60 transition-colors"
          >
            See all questions
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <CTA />
    </>
  );
}
